import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import styles from './FinancialMetrics.module.scss';
import { fetchFinancialMetrics } from '../../../../../../store/dashboard/financials/financialsApiHelpers';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { ctaPurpleRight, infoToolTip } from '../../../../../../assets/hostedassets';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { updateQueryParams } from '../../../../../../utils/searchParamUtils';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_FINANCIAL_METRICS } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useAppSelector } from '../../../../../../app/hooks';
import { ACCESS_TIERS } from '../../../../../../store/investor/constants';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import FinancialLoaderComponent from '../../../../../../common/FinancialLoader/FinancialLoaderComponent';
import clsx from 'clsx';
import A4Page from '../../../../../AicaPDFReport/A4Page/A4Page';
import { reportSectionsToShow } from '../../../../../AicaPDFReport/AicaPDFReport';
import { REPORT_SECTION_NAMES } from '../../../../../../store/pdfReport/pdfReport_constants';
import { getStartDateEndDate } from '../../../../../../utils/dateUtils';
import { DATA_FETCH_STATUS } from '../../../../../../store/dashboard/constants';
import { useElementWidth } from '../../../../../../utils/customHooks';
import AskAicaQuestionBadge from '../../../../../../common/AskAicaQuestionBadge/AskAicaQuestionBadge';
import CategorySelector from '../../../../../../common/CategorySelector/CategorySelector';
import { useAuditorCommentsAction } from '../../../../../../common/AuditorCommentsPill/useAuditorCommentsAction';
import { AUDITOR_COMMENTS_LABEL } from '../../../../../../utils/constants/commonConstants';
import ThreeDotMenu from '../../../../../../common/ThreeDotMenu/ThreeDotMenu';
import CtaPill from '../../../../../../common/_custom/CtaPill/CtaPill';
import { useAuditedType, buildConsolidationCategoryOptions } from '../../../AuditedFinancialsVisualisation/useAuditedType';
import { useAuditedStatementSectionLogic } from '../../../AuditedFinancialsVisualisation/components/AuditedStatementSection/useAuditedStatementSectionLogic';
import AIText from '../../../../../../common/AIText/AIText';
import { HEADER_TYPE } from '../../../../../../store/dashboard/auditedFinancials/constants';

const activityRatios = [
    'daysPayablesOutStanding',
    'daysInventoryOutStanding',
    'daysSalesOutStanding',
    'CashConversionCycleDays',
    'SalesByNetFixedAssets',
];
const liquidityRatios = ['CurrentRatio', 'QuickRatio'];
const solvencyRatios = ['DebtToEquity', 'TOLByTNW', 'DSCR', 'ISCR'];
const otherMetrics = [
    'RevenueGrowthPercentage',
    'GrossProfitMargin',
    'EbitaMargin',
    'ShareholdersFund',
    'TangibleNetWorth',
    'NetWorkingCapital',
    'ReceivablesPayablesGap',
    'DrawingPower',
];
const overviewMetrics = [
    'Revenue',
    'RevenueGrowthPercentage',
    'EbitaMargin',
    'PAT',
    'CashAndCashEquivalent',
    'TotalCurrentAssets',
    'TotalCurrentLiabilities',
    'NetWorkingCapital',
    'LongTermDebt',
    'ShortTermDebt',
    'TangibleNetWorth',
];
const AUDITED_STATUS = {
    PROCESSING: 'Processing',
    COMPLETE: 'Processed',
    COMPLETED: 'COMPLETED',
    FAILED: 'FAILED',
};
const UW_FETCH_FINANCIAL_METRICS = 'UW_FETCH_FINANCIAL_METRICS';
const UW_FETCH_FINANCIALS_RAW_BY_CLASSIFICATION = 'UW_FETCH_FINANCIALS_RAW_BY_CLASSIFICATION';
const AICA_GET_REPORT_STATUS_FROM_ORG_MASTER = 'AICA_GET_REPORT_STATUS_FROM_ORG_MASTER';
const BORROWINGS_CURRENT = 'borrowingsCurrent';
const BORROWINGS_NON_CURRENT = 'borrowingsNonCurrent';
const OVERVIEW = 'overview';
const DASHBOARD = 'dashboard';
// A pair per stage, because the MCA fetch message is only mounted WHILE a fetch is in flight: the
// row has two genuinely different requirements, and a single threshold measured with the message
// collapsed the pills ~400px early in the resting state — the state users are normally in.
// Measured in Gilroy at `width: max-content` against the content box (what ResizeObserver reports),
// + an 8px buffer, rounded up to the next 5. Re-measure if the row's contents change — nothing
// fails when these drift.
const HEADER_COLLAPSE_ACTIONS_WIDTH_RESTING = 795; // 783 · title + toggle | two pills
const HEADER_COLLAPSE_ACTIONS_WIDTH_FETCHING = 1160; // 1152 · the same plus the full MCA message
const HEADER_HIDE_TITLE_WIDTH_RESTING = 500; // 492 · title + toggle | menu
const HEADER_HIDE_TITLE_WIDTH_FETCHING = 745; // 734 · the same plus the shortened MCA message
const MCA_FETCH_MESSAGE = 'Fetching latest data from MCA (takes upto 24 hours)';
const MCA_FETCH_MESSAGE_COMPACT = 'Fetching latest data from MCA…';
// Measured: '-41,71,04,330' is 86.4px and the widest provisional header "FY'20-21*" 62.5px, plus the
// cell's 2rem horizontal padding = 118.4px. 7.5rem leaves a little slack. Hand-measured against
// today's number formatting — re-measure if the figures or the cell padding change.
const VALUE_COLUMN_MIN_WIDTH = '7.5rem';

const activityRatiosStart = 0;
const liquidityRatiosStart = activityRatios?.length;
const solvencyRatiosStart = liquidityRatiosStart + liquidityRatios?.length;
const otherMetricsStart = solvencyRatiosStart + solvencyRatios?.length;

function FinancialMetricsHeaderCell({ header, isPDFReport }: { header: any; isPDFReport: boolean }) {
    if (!isPDFReport && header?.type === HEADER_TYPE.PROVISIONAL_FINANCIALS) {
        return (
            <AIText
                text={(header?.name ?? '') + '*'}
                animate={false}
                style={{ display: 'block', width: '100%', textAlign: 'inherit', whiteSpace: 'nowrap' }}
            />
        );
    }
    return <>{header?.name ?? ''}</>;
}

function FinancialMetrics({
    screen = '',
    assesseeOrgId = '',
    onClose = () => {},
    isPDFReport = false,
    pdfReportSection = '',
    mcaDocApiData = {},
}: {
    screen?: string;
    assesseeOrgId?: string;
    onClose?: () => void;
    isPDFReport?: boolean;
    pdfReportSection?: string;
    mcaDocApiData?: any;
}) {
    const location = useLocation();
    const params = useParams();
    const user = useAppSelector((state) => state.user.userData);
    const [fetching, setFetching] = useState(false);
    const [financialMetricsData, setFinancialMetricsData] = useState<any>(null);
    const [noDataAvailable, setNoDataAvailable] = useState(true);
    const [auditedFinancialsStatus, setAuditedFinancialsStatus] = useState('');
    const [loaderMessage, setLoaderMessage] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams();
    const [isProcessing, setIsProcessing] = useState(false);
    const rowDataExists = !!financialMetricsData?.rows?.length;
    const isMcaCalled = mcaDocApiData?.isFetching === true && mcaDocApiData?.reportStatus === DATA_FETCH_STATUS.PROCESSING;

    // Compacting drops the title (the element the design treats as sacrificial) and shortens the MCA
    // message to lose its "(takes upto 24 hours)" tail; without that the message wrapped and blew the
    // fixed row height. Thresholds and their measurements are recorded once, at the const
    // declarations. Observed on the header itself, not the viewport: what narrows it is the
    // container, not the screen.
    // Never compacts in a PDF — A4Page is a fixed 210mm − 20mm ≈ 718px, below both breakpoints, so a
    // responsive decision would fire on a page that cannot resize and drop the report's own heading.
    // Gated here rather than at each use site, because none of the compaction applies to a fixed page.
    const [headerRef, headerWidth] = useElementWidth();
    const auditorComments = useAuditorCommentsAction(assesseeOrgId);
    const responsive = !isPDFReport && headerWidth > 0;
    const collapseWidth = isMcaCalled ? HEADER_COLLAPSE_ACTIONS_WIDTH_FETCHING : HEADER_COLLAPSE_ACTIONS_WIDTH_RESTING;
    const hideTitleWidth = isMcaCalled ? HEADER_HIDE_TITLE_WIDTH_FETCHING : HEADER_HIDE_TITLE_WIDTH_RESTING;
    const isCompact = responsive && headerWidth < collapseWidth;
    const hideTitle = responsive && headerWidth < hideTitleWidth;

    // Called unconditionally because hooks must be: on the drawer branch the sentinel is never
    // rendered, so `stuck` stays false and both effects no-op. Deliberate, not an oversight.
    // Reused as-is from AuditedStatementSection — the stuck/scroll-sync plumbing is shape-agnostic.
    const { stuck, headerWidths, setHeaderWidths, sentinelRef, cloneViewportRef, handleHorizontalScroll } =
        useAuditedStatementSectionLogic();
    const contentWidth = headerWidths[0] ?? 0;
    const tableViewportRef = useRef<HTMLDivElement | null>(null);

    // The clone's row must span the real table's full scrollable width, or its `1fr` tracks resolve
    // against the viewport instead and the columns drift out of alignment.
    useEffect(() => {
        const el = tableViewportRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return undefined;
        const sync = () => setHeaderWidths((prev) => (prev[0] === el.scrollWidth ? prev : [el.scrollWidth]));
        sync();
        const ro = new ResizeObserver(sync);
        ro.observe(el);
        return () => ro.disconnect();
    }, [setHeaderWidths, financialMetricsData]);

    const apiPollingRef: any = useRef(null);
    const noDataPollCountRef = useRef(0);
    // Tracks the current nature for polling — updated on user change and initial load.
    const currentNatureRef = useRef<string | undefined>(undefined);
    // Monotonic counter — the request that bumped it last is the only one whose response may be
    // applied. Guards against out-of-order resolution (e.g. a rapid A -> B -> A nature toggle,
    // where request 1 and request 3 share the same nature but request 1 resolves last).
    const requestGenRef = useRef(0);
    // Tracks whether a background poll's request is still in flight — lets startPolling's
    // interval skip a tick instead of piling up requests when the API is slower than 10s.
    const backgroundInFlightRef = useRef(false);

    // isBackgroundFetch: true only for the 10s poll ticks started by startPolling(). Those must
    // NOT blank already-rendered rows while the request is in flight — only foreground fetches
    // (initial load, consolidation/nature change) show the loading state.
    const fetchData = useCallback(
        (nature?: string, isBackgroundFetch = false) => {
            if (!isBackgroundFetch) setFetching(true);
            else backgroundInFlightRef.current = true;
            // Every issued request claims the next generation. If a newer request has been issued
            // by the time this one resolves, requestGenRef.current will have moved past requestGen
            // and the response below is dropped rather than un-blanking/overwriting fresher state.
            const requestGen = ++requestGenRef.current;
            const { startDate } = getStartDateEndDate();
            fetchFinancialMetrics(
                {
                    orgId: assesseeOrgId,
                    startDate,
                    endDate: null,
                    frequency: 'Yearly',
                    metricsList:
                        screen === 'overview'
                            ? [...overviewMetrics]
                            : [...activityRatios, ...liquidityRatios, ...solvencyRatios, ...otherMetrics],
                    round: true,
                    nature,
                    ...(!isPDFReport && { type: 'auditedMissingProvisionals' }),
                },
                {
                    onSuccess: (data) => {
                        if (isBackgroundFetch) backgroundInFlightRef.current = false;
                        if (requestGenRef.current !== requestGen) return;
                        transformAndUpdateData({ ...data });
                    },
                    onError: () => {
                        if (isBackgroundFetch) backgroundInFlightRef.current = false;
                        if (requestGenRef.current !== requestGen) return;
                        setFetching(false);
                    },
                },
            );
        },
        [assesseeOrgId, screen, isPDFReport],
    );

    // Called by useAuditedType when user explicitly changes the dropdown
    const onConsolidationChange = useCallback(
        (nature: string) => {
            currentNatureRef.current = nature;
            stopPolling();
            fetchData(nature);
        },
        [fetchData],
    );

    const { auditedType, auditedTypeOptionsObject, handleConsolidationChange, setAuditedTypeFromYears } = useAuditedType(
        onConsolidationChange,
        assesseeOrgId,
    );

    useEffect(() => {
        if (isPDFReport && financialMetricsData && Array.isArray(financialMetricsData.headers)) {
            const headersLength = financialMetricsData.headers.length;
            if (headersLength > 1) {
                const updatedHeaders = [
                    financialMetricsData.headers[0],
                    ...(headersLength > 3 ? [financialMetricsData.headers[headersLength - 3]] : []),
                    ...(headersLength > 2 ? [financialMetricsData.headers[headersLength - 2]] : []),
                    financialMetricsData.headers[headersLength - 1],
                ];

                if (JSON.stringify(financialMetricsData.headers) !== JSON.stringify(updatedHeaders)) {
                    setFinancialMetricsData((existing) => ({
                        ...existing,
                        headers: updatedHeaders,
                    }));
                }
            }
        }
    }, [isPDFReport, financialMetricsData]);

    useEffect(() => {
        if (assesseeOrgId && screen) {
            fetchData(undefined);
        }
        return () => {
            stopPolling();
        };
    }, [assesseeOrgId, screen]);

    useEffect(() => {
        if (auditedFinancialsStatus === AUDITED_STATUS.PROCESSING) setIsProcessing(true);
        else setIsProcessing(false);
    }, [auditedFinancialsStatus, noDataAvailable]);

    const startPolling = () => {
        if (!apiPollingRef.current) {
            apiPollingRef.current = setInterval(() => {
                // Skip the tick while the previous background request is still pending, instead
                // of superseding it via requestGenRef and starving the table of an update.
                if (backgroundInFlightRef.current) return;
                fetchData(currentNatureRef.current, true);
            }, 10000);
        }
    };

    const stopPolling = () => {
        if (apiPollingRef.current) {
            clearInterval(apiPollingRef.current);
            apiPollingRef.current = null;
        }
    };

    const transformAndUpdateData = (data) => {
        let uwFinancialMetrics = data?.[UW_FETCH_FINANCIAL_METRICS]?.data?.data;
        let uwRawByClassification = data?.[UW_FETCH_FINANCIALS_RAW_BY_CLASSIFICATION]?.data;
        let auditedTaskStatus = data?.[AICA_GET_REPORT_STATUS_FROM_ORG_MASTER]?.reportStatus?.auditedFinancialsStatus;

        const consolidatedFY = uwFinancialMetrics?.latestConsolidatedFY ?? null;
        const standaloneFY = uwFinancialMetrics?.latestStandaloneFY ?? null;

        setAuditedTypeFromYears(consolidatedFY, standaloneFY);

        if (!auditedTaskStatus) setAuditedFinancialsStatus(AUDITED_STATUS.COMPLETE);

        let borrowingsCurrent = uwRawByClassification?.rows?.find((row_data) => row_data?.parent === BORROWINGS_CURRENT);
        let borrowingsNonCurrent = uwRawByClassification?.rows?.find((row_data) => row_data?.parent === BORROWINGS_NON_CURRENT);

        let noDataAvailable = true;
        let updatedRows =
            uwFinancialMetrics?.rows?.map((row) => {
                let rowData = {
                    ...row,
                    ...(row?.metricName === 'Long Term Debt' && borrowingsNonCurrent),
                    ...(row?.metricName === 'Short Term Debt' && borrowingsCurrent),
                };
                uwFinancialMetrics?.headers?.forEach((header, headerIndex) => {
                    if (noDataAvailable && headerIndex > 0 && (rowData?.[header.key] || rowData?.[header.key] === 0)) {
                        noDataAvailable = false;
                    }
                });
                return rowData;
            }) || [];
        uwFinancialMetrics.rows = updatedRows;
        if (auditedTaskStatus === AUDITED_STATUS.COMPLETE) {
            if (noDataAvailable && !noDataPollCountRef.current) {
                startPolling();
                noDataPollCountRef.current = 1;
            } else {
                setAuditedFinancialsStatus(AUDITED_STATUS.COMPLETE);
                stopPolling();
            }
        } else if (auditedTaskStatus === AUDITED_STATUS.PROCESSING) {
            setAuditedFinancialsStatus(AUDITED_STATUS.PROCESSING);
            startPolling();
        } else {
            setAuditedFinancialsStatus(AUDITED_STATUS.COMPLETE);
            stopPolling();
        }

        setNoDataAvailable(noDataAvailable);
        // Bail out of the re-render (and the row-remount that follows) when a poll tick brings
        // back data identical to what's already rendered.
        setFinancialMetricsData((prev) => (prev && isEqual(prev, uwFinancialMetrics) ? prev : uwFinancialMetrics));
        setFetching(false);
    };

    const getSeparatorRowText = (rowIndex) => {
        switch (rowIndex) {
            case activityRatiosStart:
                return 'Activity Ratios';
            case liquidityRatiosStart:
                return 'Liquidity Ratios';
            case solvencyRatiosStart:
                return 'Solvency Ratios';
            case otherMetricsStart:
                return 'Other Derived Metrics';
            default:
                return '';
        }
    };

    const getSeparatorRow = (rowIndex, screenName) => {
        if (screenName === OVERVIEW) return null;
        if ([activityRatiosStart, liquidityRatiosStart, solvencyRatiosStart, otherMetricsStart]?.includes(rowIndex))
            return <div className={styles.SeparatorRow}>{getSeparatorRowText(rowIndex)}</div>;
        else return null;
    };

    const getRowText = (value, unit = '', roundOffRequired = false) => {
        if (typeof value === 'number') {
            if (unit === '%' || roundOffRequired) {
                return parseFloat(value.toFixed(1))?.toLocaleString('en-IN') + unit;
            }
            return parseFloat(value.toFixed(2))?.toLocaleString('en-IN') + unit;
        } else return value ?? '-';
    };

    const showFinancialSummary = () => {
        Mixpanel.track(AICA_FINANCIAL_METRICS, {
            role: user.accountType,
            investorOrgId: user.investorOrgId,
            companyOrgId: params.id,
            AICASpecificSection: 'Overview',
            AICASection: ACCESS_TIERS.SCAN,
        });
        updateQueryParams(searchParams, setSearchParams, { showFinancialMetrics: true }, location);
    };

    const getToolTipContent = (row) => {
        return (
            <div>
                <div>
                    {row?.definitionText && (
                        <>
                            <span className={styles.Bold}>Definition: </span>
                            {row?.definitionText}
                        </>
                    )}
                </div>
                <br />
                <div>
                    {row?.calculationText && (
                        <>
                            <span className={styles.Bold}>Calculation: </span>
                            {row?.calculationText}
                        </>
                    )}
                </div>
                <br />
                <div>
                    {row?.note && (
                        <>
                            <span className={styles.Bold}>Note: </span>
                            {row?.note}
                        </>
                    )}
                </div>
            </div>
        );
    };

    useEffect(() => {
        showLoaderMessage(noDataAvailable);
    }, [isMcaCalled, noDataAvailable, isCompact]);

    function showLoaderMessage(noDataAvailable) {
        if (isMcaCalled) setLoaderMessage(isCompact ? MCA_FETCH_MESSAGE_COMPACT : MCA_FETCH_MESSAGE);
        else if (noDataAvailable) setLoaderMessage('No Data Available');
        else setLoaderMessage('');
    }

    // Same segmented tab toggle used by AuditedFinancialsVisualisation / AuditorCommentsDrawer,
    // reused here by both the drawer header and the Overview tab header.
    const consolidatedCategorySelector = () => (
        <CategorySelector
            categoryOptions={buildConsolidationCategoryOptions(auditedTypeOptionsObject)}
            selectedCategory={auditedType}
            changeSelectedTab={(type) => handleConsolidationChange(type)}
        />
    );

    const getTableComponent = (screenName = '') => {
        if (!financialMetricsData?.headers?.length) {
            return <div className={styles.Nodata}>Data not available for last three financial years.</div>;
        }

        // Fixed floors rather than content-derived ones, so every row (each its own grid) resolves
        // to identical column widths regardless of content — otherwise the first column's width
        // varies per row and the left border zigzags.
        // On the tab, value columns floor at VALUE_COLUMN_MIN_WIDTH: below it a full-crore figure is
        // cut mid-digit, which reads as a different number, and the table scrolls horizontally
        // instead. The drawer has no such scroller, so it keeps the original 0 floor.
        const repeatCount = Math.max(0, financialMetricsData.headers.length - 1);
        const isOverview = screenName === OVERVIEW;
        const valueColumnFloor = isOverview ? VALUE_COLUMN_MIN_WIDTH : '0';
        const gridTemplateColumns = `minmax(0, 2fr) repeat(${repeatCount}, minmax(${valueColumnFloor}, 1fr))`;
        const headRow = (
            <div className={styles.TableHeadRow} style={{ gridTemplateColumns }}>
                {financialMetricsData?.headers?.map((header, i) => (
                    <div key={i} className={styles.TableHeadRowItem}>
                        <FinancialMetricsHeaderCell header={header} isPDFReport={isPDFReport} />
                    </div>
                ))}
            </div>
        );
        // The table scrolls horizontally, so its own head row can only stick to that scroller —
        // useless. A clone in a band outside it pins against the page instead, scroll-synced so the
        // two stay column-aligned. Same approach as AuditedStatementSection.
        const cloneBand = isOverview && stuck && contentWidth > 0 && (
            <div className={styles.StickyHeadBand} aria-hidden="true">
                <div className={styles.HeadCloneViewport} ref={cloneViewportRef} data-testid="financial-metrics-head-clone">
                    {/* Same .Table wrapper as the real row — the grid and cell rules are nested under it. */}
                    <div className={clsx(styles.Table, styles.overviewScreen)} style={{ width: contentWidth }}>
                        {headRow}
                    </div>
                </div>
            </div>
        );
        return (
            <>
                {cloneBand}
                <div
                    className={clsx(styles.tableContainer, { [styles.scrollable]: isOverview })}
                    ref={tableViewportRef}
                    onScroll={(e) => handleHorizontalScroll(e.currentTarget.scrollLeft)}
                >
                    <div
                        className={clsx(styles.Table, {
                            [styles.overviewScreen]: isOverview,
                        })}
                    >
                        {stuck && isOverview ? <div className={styles.HeadRowHidden}>{headRow}</div> : headRow}

                        {fetching ? null : rowDataExists && !noDataAvailable ? (
                            financialMetricsData?.rows?.map((row, i) => (
                                <React.Fragment key={i}>
                                    {getSeparatorRow(i, screenName)}
                                    <div className={styles.TableBodyRow} style={{ gridTemplateColumns }}>
                                        {financialMetricsData?.headers?.map((header, j) => (
                                            <div
                                                className={clsx(
                                                    {
                                                        [styles.sectionEnd]: (i == 3 || i == 7) && screenName === OVERVIEW,
                                                        [styles.audited]:
                                                            !isPDFReport && j !== 0 && header.type === HEADER_TYPE.AUDITED_FINANCIALS,
                                                        [styles.provisional]:
                                                            !isPDFReport && j !== 0 && header.type === HEADER_TYPE.PROVISIONAL_FINANCIALS,
                                                    },
                                                    styles.TableBodyRowItem,
                                                )}
                                                key={j}
                                            >
                                                <div className={styles.rowText}>
                                                    {getRowText(row?.[header.key], row?.unit, row?.roundOffRequired)}
                                                </div>
                                                {j === 0 && !isPDFReport && (row?.definitionText || row?.calculationText) && (
                                                    <Tooltip
                                                        content={getToolTipContent(row)}
                                                        direction="right"
                                                        // The table clips its overflow for the rounded corners, so an in-flow
                                                        // tip is cut off at the table edge — worst on the last row.
                                                        usePortal
                                                    >
                                                        <img src={infoToolTip} height={'14px'} />
                                                    </Tooltip>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </React.Fragment>
                            ))
                        ) : (
                            <div className={styles.Nodata}>Data not available for last three financial years.</div>
                        )}
                    </div>
                </div>
                {rowDataExists &&
                    !noDataAvailable &&
                    !isPDFReport &&
                    financialMetricsData?.headers?.some((h) => h?.type === HEADER_TYPE.PROVISIONAL_FINANCIALS) && (
                        <div className={styles.provisionalNote}>*Note: Provisional metrics are updated basis unaudited data.</div>
                    )}
            </>
        );
    };

    // Compact: every action leaves the row for the menu, so all three financials tabs collapse the
    // same way. At full width they stay inline as pills.
    const ctaElement = (
        <>
            {isCompact ? (
                <ThreeDotMenu
                    color="#6021b3"
                    menuItems={[
                        { name: 'Financial Ratios', onClick: () => showFinancialSummary() },
                        ...(auditorComments.isAvailable ? [{ name: AUDITOR_COMMENTS_LABEL, onClick: auditorComments.open }] : []),
                    ]}
                />
            ) : (
                <>
                    <CtaPill label="Financial Ratios" icon={ctaPurpleRight} onClick={showFinancialSummary} />
                    {auditorComments.isAvailable && (
                        <CtaPill label={AUDITOR_COMMENTS_LABEL} icon={ctaPurpleRight} onClick={auditorComments.open} />
                    )}
                </>
            )}
        </>
    );

    const renderComponent = () => (
        <>
            <div className={styles.HeadingContainer} ref={headerRef}>
                <div className={styles.TitleGroup}>
                    {!hideTitle && <h3>Financial Summary</h3>}
                    {!isPDFReport && !noDataAvailable && consolidatedCategorySelector()}
                    {!isMcaCalled && loaderMessage && <div className={styles.statusMessage}>{loaderMessage}</div>}
                </div>
                <div className={styles.ActionsGroup}>
                    {isMcaCalled && !isPDFReport && (
                        <div className={styles.loaderMessage}>
                            <LoadingSpinner color="#6021B3" />
                            {loaderMessage}
                        </div>
                    )}
                    {!noDataAvailable && !isPDFReport && ctaElement}
                </div>
            </div>

            {/* Rendered unconditionally: the observer that watches it is set up once on mount, so a
                sentinel that only appears with the data would never be observed. */}
            {/* Deliberately outside BOTH the isCompact branch and the !noDataAvailable guard above:
                one hook instance owns this drawer, and neither the row compacting nor a poll
                resolving with no rows may unmount drawer-local work that is open. */}
            {auditorComments.drawer}

            <div className={styles.StickySentinel} aria-hidden="true" ref={sentinelRef} />
            {!noDataAvailable && getTableComponent(screen)}
        </>
    );

    const render =
        screen === DASHBOARD ? (
            isPDFReport ? (
                <>{getTableComponent(screen)}</>
            ) : (
                <SideDrawer onClose={onClose} width="70%">
                    <SideDrawer.Header>
                        <div className={clsx({ [styles.dashboard]: screen === DASHBOARD }, styles.Header)}>
                            <div className={styles.HeaderLeft}>
                                <span className={styles.HeaderTitle}>
                                    Financial Metrics{' '}
                                    {!financialMetricsData?.headers?.some((h) => h?.type === HEADER_TYPE.PROVISIONAL_FINANCIALS) && (
                                        <span className={styles.Sub}>| From Audited Financials</span>
                                    )}
                                </span>
                                {!noDataAvailable && consolidatedCategorySelector()}
                            </div>
                            {isMcaCalled && (
                                <span className={styles.LoaderMessage}>
                                    <LoadingSpinner color="#6021B3" />
                                    Fetching latest data from MCA (takes upto 24 hours)
                                </span>
                            )}
                        </div>
                    </SideDrawer.Header>
                    <div className={styles.FinancialMetrics}>{getTableComponent(screen)}</div>
                </SideDrawer>
            )
        ) : (
            renderComponent()
        );

    if (isPDFReport)
        return !reportSectionsToShow.includes(pdfReportSection) || noDataAvailable ? (
            <></>
        ) : (
            <A4Page sectionName={pdfReportSection}>
                {screen === DASHBOARD ? (
                    <div className={styles.PDFCard}>
                        <div className={styles.Top}>
                            <div className={styles.Title}>{pdfReportSection}</div>
                        </div>
                        <div className={styles.Body}>{render}</div>
                    </div>
                ) : (
                    render
                )}
            </A4Page>
        );
    else return render;
}

export default FinancialMetrics;
